import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Countries that show 404 (completely hidden - as if site doesn't exist)
const HARD_BLOCKED_COUNTRIES = ["CN", "JP", "RU"];

// Countries redirected to region-blocked page (currently none)
const BLOCKED_COUNTRIES: string[] = [];

// Countries explicitly allowed (overrides continent blocklist)
const ALLOWED_COUNTRIES = ["PH", "IN"];

// Allowed continents
// NA = North America (US, Canada, Mexico)
// EU = Europe
// AF = Africa
// SA = South America
// OC = Oceania (Australia, New Zealand, etc.)
const ALLOWED_CONTINENTS = ["NA", "EU", "AF", "SA", "OC"];

// Paths that should NOT trigger geo-blocking
const EXCLUDED_PATHS = ["/region-blocked"];

// proxycheck.io API key (optional - works without key but with lower limits)
const PROXYCHECK_API_KEY = import.meta.env.VITE_PROXYCHECK_API_KEY;

interface ProxycheckResponse {
  status: string;
  [ip: string]: {
    proxy: string;
    type?: string;
    provider?: string;
    country?: string;
    isocode?: string;
    continent?: string;
  } | string;
}

export function useGeoBlock() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    // SSR safety - skip if no window
    if (typeof window === 'undefined') {
      setIsChecking(false);
      return;
    }

    // Skip if we're already on an excluded path
    if (EXCLUDED_PATHS.some(path => location.pathname.startsWith(path))) {
      setIsChecking(false);
      return;
    }

    // Check if we've already verified this session
    const geoChecked = sessionStorage.getItem("geo_checked");
    const geoBlocked = sessionStorage.getItem("geo_blocked");
    
    if (geoChecked === "true") {
      if (geoBlocked === "true") {
        setIsBlocked(true);
        navigate("/region-blocked", { replace: true });
      }
      setIsChecking(false);
      return;
    }

    // Perform geo and VPN check
    const checkAccess = async () => {
      try {
        // First, get IP and geo data from ipapi.co
        const geoResponse = await fetch("https://ipapi.co/json/", {
          signal: AbortSignal.timeout(5000),
        });
        
        if (!geoResponse.ok) {
          throw new Error("Geo lookup failed");
        }
        
        const geoData = await geoResponse.json();
        const countryCode = geoData.country_code;
        const continentCode = geoData.continent_code;
        const userIP = geoData.ip;
        
        console.log("Geo check result:", { country: countryCode, continent: continentCode, ip: userIP });
        
        // Check if hard blocked (show 404 - site doesn't exist for them)
        if (HARD_BLOCKED_COUNTRIES.includes(countryCode)) {
          console.log("Country hard blocked (404):", countryCode);
          sessionStorage.setItem("geo_checked", "true");
          sessionStorage.setItem("geo_blocked", "true");
          setIsBlocked(true);
          navigate("/page-not-found-404", { replace: true });
          return;
        }
        
        // Check if soft blocked (show region-blocked page)
        if (BLOCKED_COUNTRIES.includes(countryCode)) {
          console.log("Country blocked:", countryCode);
          sessionStorage.setItem("geo_checked", "true");
          sessionStorage.setItem("geo_blocked", "true");
          setIsBlocked(true);
          navigate("/region-blocked", { replace: true });
          return;
        }
        
        // Check if explicitly allowed (overrides continent block)
        if (ALLOWED_COUNTRIES.includes(countryCode)) {
          console.log("Country explicitly allowed:", countryCode);
          sessionStorage.setItem("geo_checked", "true");
          sessionStorage.setItem("geo_blocked", "false");
          setIsChecking(false);
          return;
        }
        
        // Check if continent is allowed
        if (!ALLOWED_CONTINENTS.includes(continentCode)) {
          console.log("Continent not in allowlist:", continentCode);
          sessionStorage.setItem("geo_checked", "true");
          sessionStorage.setItem("geo_blocked", "true");
          setIsBlocked(true);
          navigate("/region-blocked", { replace: true });
          return;
        }
        
        // VPN/Proxy detection using proxycheck.io
        if (userIP) {
          try {
            const proxycheckUrl = PROXYCHECK_API_KEY
              ? `https://proxycheck.io/v2/${userIP}?key=${PROXYCHECK_API_KEY}&vpn=1&asn=1`
              : `https://proxycheck.io/v2/${userIP}?vpn=1`;
            
            const vpnResponse = await fetch(proxycheckUrl, {
              signal: AbortSignal.timeout(5000),
            });
            
            if (vpnResponse.ok) {
              const vpnData: ProxycheckResponse = await vpnResponse.json();
              
              if (vpnData.status === "ok") {
                const ipData = vpnData[userIP];
                
                if (ipData && typeof ipData === "object" && ipData.proxy === "yes") {
                  console.log("VPN/Proxy detected:", {
                    type: ipData.type,
                    provider: ipData.provider,
                  });
                  sessionStorage.setItem("geo_checked", "true");
                  sessionStorage.setItem("geo_blocked", "true");
                  setIsBlocked(true);
                  navigate("/region-blocked", { replace: true });
                  return;
                }
              }
            }
          } catch {
            // VPN check failed silently - continue without blocking
            // This is expected when CORS blocks the request (e.g., from Lighthouse)
          }
        }
        
        // All checks passed - allow access
        console.log("Access allowed for:", { country: countryCode, continent: continentCode });
        sessionStorage.setItem("geo_checked", "true");
        sessionStorage.setItem("geo_blocked", "false");
      } catch (error) {
        console.error("Geo check error:", error);
        // On error, allow access (fail open)
        sessionStorage.setItem("geo_checked", "true");
        sessionStorage.setItem("geo_blocked", "false");
      } finally {
        setIsChecking(false);
      }
    };

    checkAccess();
  }, [location.pathname, navigate]);

  return { isChecking, isBlocked };
}
